import { useState, useRef, useMemo, useEffect } from "react";
import {
  X,
  Upload,
  ImagePlus,
  Star,
  Trash2,
  AlertTriangle,
  Info,
  Check,
  FolderKanban,
  User,
  ShieldAlert,
  Sparkles,
  Layers,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import MediaFrame from "@/components/common/MediaFrame";
import AssetPicker from "@/components/common/AssetPicker";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { useStudio, useLookups } from "@/store/StudioContext";
import { CHARACTER_KINDS, CHARACTER_VIEW_TYPES, uid } from "@/domain/entities";
import { validateHandleCollision, normalizeHandle } from "@/domain/librarySelectors";
import { uploadAsset, api } from "@/lib/api";
import { t } from "@/i18n";
import { toast } from "sonner";

const slug = (s = "") =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 32);

export const CharacterModal = ({
  open,
  onOpenChange,
  character = null,
  defaultProjectId = null,
  onSave,
}) => {
  const { state, dispatch } = useStudio();
  const { assetById, projectById } = useLookups();
  const fileInputRef = useRef(null);

  const isEditing = Boolean(character?.id);

  const fallbackProjectId = state.projects?.[0]?.id || null;

  // Construir estado inicial del formulario
  const initialForm = useMemo(() => {
    if (character) {
      let views = Array.isArray(character.views) ? [...character.views] : [];
      let masterAssetId = character.masterAssetId ?? null;

      if (views.length === 0 && masterAssetId) {
        views.push({
          id: uid("vw"),
          assetId: masterAssetId,
          viewType: "front",
          isMaster: true,
          label: t.characters.viewTypes?.front || "Frontal",
        });
      }
      if (Array.isArray(character.viewAssetIds)) {
        for (const vId of character.viewAssetIds) {
          if (vId && vId !== masterAssetId && !views.some((v) => v.assetId === vId)) {
            views.push({
              id: uid("vw"),
              assetId: vId,
              viewType: "other",
              isMaster: false,
              label: t.characters.viewTypes?.other || "Otra",
            });
          }
        }
      }

      return {
        id: character.id || null,
        name: character.name || "",
        handle: character.handle || "",
        handleTouched: Boolean(character.handle),
        kind: character.kind || "character",
        description: character.description || "",
        outfit: character.outfit || character.baseWardrobe || "",
        projectId: character.projectId || defaultProjectId || fallbackProjectId,
        scope: character.scope || (character.projectId ? "project" : "pending_review"),
        views,
        prefilled: character.prefilled || false,
      };
    }

    return {
      id: null,
      name: "",
      handle: "",
      handleTouched: false,
      kind: "character",
      description: "",
      outfit: "",
      projectId: defaultProjectId || fallbackProjectId,
      scope: defaultProjectId || fallbackProjectId ? "project" : "pending_review",
      views: [],
      prefilled: false,
    };
  }, [character, defaultProjectId, fallbackProjectId]);

  const [form, setForm] = useState(initialForm);
  const [pickAssetOpen, setPickAssetOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  // Autocompletar con visión
  const [activeVisionModel, setActiveVisionModel] = useState(null);
  const [analyzingImage, setAnalyzingImage] = useState(false);
  const [analysisProposal, setAnalysisProposal] = useState(null);

  // Sincronizar y limpiar formulario al abrir/cerrar el modal o al cambiar el recurso objetivo
  useEffect(() => {
    setForm(initialForm);
    setAnalysisProposal(null);
    setUploadError(null);
    setUploading(false);
    setDiscardConfirmOpen(false);
  }, [open, initialForm]);

  // Cargar modelo con visión activo si existe
  useEffect(() => {
    let active = true;
    const fetchVision = async () => {
      try {
        const models = await api.getAssistantModels();
        if (active && Array.isArray(models)) {
          const vModel = models.find((m) => m.capabilities?.includes("vision"));
          setActiveVisionModel(vModel || null);
        }
      } catch {
        if (active) setActiveVisionModel(null);
      }
    };
    if (open) {
      fetchVision();
    }
    return () => {
      active = false;
    };
  }, [open]);

  // Proyecto de destino claramente identificado para la portada
  const targetProject = useMemo(() => {
    return (
      (state.projects || []).find((p) => p.id === form.projectId) ||
      (state.projects || [])[0] ||
      null
    );
  }, [state.projects, form.projectId]);

  const targetProjectName = targetProject ? (targetProject.name || targetProject.id) : (t.characters.noProject || "Proyecto");

  // Detección de cambios sucios (isDirty)
  const isDirty = useMemo(() => {
    if (form.name !== initialForm.name) return true;
    if (form.handle !== initialForm.handle) return true;
    if (form.kind !== initialForm.kind) return true;
    if (form.description !== initialForm.description) return true;
    if (form.outfit !== initialForm.outfit) return true;
    if (form.projectId !== initialForm.projectId) return true;
    if (form.views.length !== initialForm.views.length) return true;
    for (let i = 0; i < form.views.length; i++) {
      const v = form.views[i];
      const iv = initialForm.views[i];
      if (!iv || v.assetId !== iv.assetId || v.viewType !== iv.viewType || v.isMaster !== iv.isMaster) {
        return true;
      }
    }
    return false;
  }, [form, initialForm]);

  // Validación de colisión de handle
  const collisionResult = useMemo(() => {
    if (!form.handle.trim()) return { valid: true };
    return validateHandleCollision(state, {
      handle: form.handle,
      projectId: form.projectId,
      scope: form.scope,
      excludeId: form.id,
    });
  }, [state, form.handle, form.projectId, form.scope, form.id]);

  // Vista principal activa
  const masterView = useMemo(() => {
    return form.views.find((v) => v.isMaster) || form.views[0] || null;
  }, [form.views]);

  // Descartar propuesta si cambia la imagen principal
  const masterViewRef = useRef(masterView);
  useEffect(() => {
    masterViewRef.current = masterView;
    if (analysisProposal && masterView?.assetId && analysisProposal.assetId !== masterView.assetId) {
      setAnalysisProposal(null);
      toast.warning(t.characters.imageChangedDuringAnalysis);
    }
  }, [masterView, analysisProposal]);

  // Manejadores de campos
  const handleNameChange = (e) => {
    const val = e.target.value;
    setForm((prev) => ({
      ...prev,
      name: val,
      ...(!prev.handleTouched ? { handle: slug(val) } : {}),
    }));
  };

  const handleHandleChange = (e) => {
    const val = slug(e.target.value);
    setForm((prev) => ({
      ...prev,
      handle: val,
      handleTouched: true,
    }));
  };

  // Manejo de Vistas
  const handleSetMaster = (viewId) => {
    setForm((prev) => ({
      ...prev,
      views: prev.views.map((v) => ({
        ...v,
        isMaster: v.id === viewId,
      })),
    }));
  };

  const handleUpdateViewType = (viewId, newType) => {
    const typeObj = CHARACTER_VIEW_TYPES.find((t) => t.id === newType);
    setForm((prev) => ({
      ...prev,
      views: prev.views.map((v) =>
        v.id === viewId
          ? {
              ...v,
              viewType: newType,
              label: typeObj ? typeObj.label : v.label,
            }
          : v
      ),
    }));
  };

  const handleRemoveView = (viewId) => {
    setForm((prev) => {
      const remaining = prev.views.filter((v) => v.id !== viewId);
      const wasMaster = prev.views.find((v) => v.id === viewId)?.isMaster;
      if (wasMaster && remaining.length > 0) {
        remaining[0] = { ...remaining[0], isMaster: true };
      }
      return { ...prev, views: remaining };
    });
  };

  const handleAddPickedAsset = (asset) => {
    if (!asset?.id) return;
    setForm((prev) => {
      // Evitar duplicar el mismo asset en las vistas
      if (prev.views.some((v) => v.assetId === asset.id)) {
        toast.info(t.characters.alreadyAdded || "Esta imagen ya está en las vistas del personaje.");
        return prev;
      }
      const isFirst = prev.views.length === 0;
      const newView = {
        id: uid("vw"),
        assetId: asset.id,
        viewType: isFirst ? "front" : "other",
        isMaster: isFirst,
        label: isFirst ? (t.characters.viewTypes?.front || "Frontal") : (t.characters.viewTypes?.other || "Otra"),
      };
      return {
        ...prev,
        views: [...prev.views, newView],
      };
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input
    e.target.value = "";
    setUploading(true);
    setUploadError(null);

    try {
      const uploaded = await uploadAsset(file, file.name);
      // Registrar activo en el estado del estudio
      const newAsset = {
        id: uploaded.id,
        type: "image",
        url: uploaded.url,
        width: uploaded.width,
        height: uploaded.height,
        source: "upload",
        label: file.name || "Foto de referencia",
        projectId: form.projectId || null,
        isDemo: false,
      };
      dispatch({ type: "ASSET_ADD", payload: newAsset });

      // Añadir como vista del personaje
      handleAddPickedAsset(newAsset);
      toast.success(t.characters.uploadedSuccess || "Imagen subida y añadida como vista.");
    } catch (err) {
      const msg = err.message || t.common.error;
      setUploadError(msg);
      toast.error(`Error al subir imagen: ${msg}`);
    } finally {
      setUploading(false);
    }
  };

  // Cierre seguro con confirmación de descarte
  const handleRequestClose = () => {
    if (isDirty) {
      setDiscardConfirmOpen(true);
    } else {
      onOpenChange(false);
    }
  };

  const [settingCover, setSettingCover] = useState(false);

  // Acción explícita para usar una vista como portada del proyecto
  const handleSetProjectCover = async (assetId) => {
    if (!assetId || settingCover) return;
    if (isDirty) {
      toast.error(t.characters.saveBeforeCover || "Guarda los cambios de la ficha antes de aplicar la imagen como portada del proyecto.");
      return;
    }
    if (!targetProject?.id) {
      toast.error(t.characters.assignProjectBeforeCover || "Asigna un proyecto al personaje para fijar su portada.");
      return;
    }

    const asset = assetById[assetId];
    if (!asset) {
      toast.error(t.common.error);
      return;
    }

    if (asset.projectId && asset.projectId !== targetProject.id && asset.scope !== "global") {
      toast.error(t.characters.foreignAssetCoverError || "El activo no pertenece ni está autorizado para este proyecto.");
      return;
    }

    if (targetProject.coverAssetId === assetId) {
      toast.info(
        typeof t.characters.alreadyProjectCover === "function"
          ? t.characters.alreadyProjectCover(targetProjectName)
          : `Esta imagen ya es la portada de "${targetProjectName}".`
      );
      return;
    }

    // Preparar estado actualizado:
    // 1. Asignar coverAssetId al proyecto objetivo
    // 2. Si el activo no tenía projectId asignado, vincularlo formalmente a targetProject.id
    const updatedProjects = (state.projects || []).map((p) =>
      p.id === targetProject.id ? { ...p, coverAssetId: assetId } : p
    );
    const updatedAssets = !asset.projectId
      ? (state.assets || []).map((a) => (a.id === assetId ? { ...a, projectId: targetProject.id } : a))
      : state.assets || [];

    const nextState = {
      ...state,
      projects: updatedProjects,
      assets: updatedAssets,
    };

    setSettingCover(true);
    try {
      if (!asset.projectId) {
        dispatch({
          type: "ASSET_UPDATE",
          payload: { id: assetId, patch: { projectId: targetProject.id } },
        });
      }
      dispatch({
        type: "PROJECT_UPDATE",
        payload: {
          id: targetProject.id,
          patch: { coverAssetId: assetId },
        },
      });

      // Persistir inmediatamente y confirmar con el backend antes de anunciar éxito
      const { lastError, ...persisted } = nextState;
      await api.saveState(persisted);

      toast.success(
        typeof t.characters.coverUpdated === "function"
          ? t.characters.coverUpdated(targetProjectName)
          : `Imagen establecida como portada de "${targetProjectName}".`
      );
    } catch (err) {
      toast.error(
        err?.message
          ? `Error al guardar portada: ${err.message}`
          : "No se pudo guardar la portada en el servidor."
      );
    } finally {
      setSettingCover(false);
    }
  };

  // Autocompletar identidad y vestuario desde imagen
  const handleAnalyzeImage = async () => {
    if (!masterView?.assetId) {
      toast.error("Selecciona una vista principal antes de iniciar el análisis.");
      return;
    }
    const assetToAnalyze = masterView.assetId;
    setAnalyzingImage(true);
    setAnalysisProposal(null);

    try {
      const isMock = Boolean(window.__VEYRA_ENABLE_MOCK_VISION__ || activeVisionModel?.is_mock);
      const res = await api.analyzeCharacterImage({
        project_id: targetProject?.id || form.projectId || "prj_default",
        asset_id: assetToAnalyze,
        mock_mode: isMock,
      });

      if (res?.status === "success") {
        if (masterViewRef.current?.assetId !== assetToAnalyze) {
          toast.warning(t.characters.imageChangedDuringAnalysis);
          return;
        }
        setAnalysisProposal({
          identity_traits: res.identity_traits,
          outfit: res.outfit,
          assetId: assetToAnalyze,
        });
        toast.success("Propuesta de análisis visual generada.");
      } else if (res?.status === "missing_vision_connection") {
        toast.info(res.message || t.characters.noVisionModelNotice);
      } else {
        toast.info(res?.message || "Análisis no disponible.");
      }
    } catch (err) {
      toast.error(`Error en análisis de imagen: ${err.message || t.common.error}`);
    } finally {
      setAnalyzingImage(false);
    }
  };

  const handleApplyIdentity = () => {
    if (!analysisProposal?.identity_traits) return;
    setForm((prev) => ({
      ...prev,
      description: analysisProposal.identity_traits,
    }));
    toast.success(t.characters.appliedToIdentity || "Identidad actualizada.");
  };

  const handleApplyOutfit = () => {
    if (!analysisProposal?.outfit) return;
    setForm((prev) => ({
      ...prev,
      outfit: analysisProposal.outfit,
    }));
    toast.success(t.characters.appliedToOutfit || "Vestuario actualizado.");
  };

  const handleSave = () => {
    if (!form.name.trim()) {
      toast.error(t.characters.nameRequired);
      return;
    }
    if (!form.handle.trim()) {
      toast.error(t.characters.handleRequired);
      return;
    }
    if (!collisionResult.valid) {
      toast.error(collisionResult.reason);
      return;
    }

    const master = form.views.find((v) => v.isMaster) || form.views[0];
    const masterAssetId = master?.assetId || null;
    const viewAssetIds = form.views.map((v) => v.assetId).filter(Boolean);

    const payload = {
      ...(form.id ? { id: form.id } : {}),
      name: form.name.trim(),
      handle: form.handle.trim(),
      kind: form.kind,
      description: form.description.trim(),
      outfit: form.outfit.trim(),
      baseWardrobe: form.outfit.trim(),
      projectId: form.projectId,
      scope: form.projectId ? "project" : "pending_review",
      views: form.views,
      masterAssetId,
      viewAssetIds,
    };

    onSave(payload);
    setForm(initialForm);
    setAnalysisProposal(null);
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => (!isOpen ? handleRequestClose() : null)}>
        <DialogContent
          hideCloseButton={true}
          data-testid="character-modal"
          className="flex flex-col max-h-[90vh] w-[95vw] md:w-full max-w-4xl overflow-hidden border-[#303030] bg-[#141414] p-0 text-[#f4f1ec] shadow-2xl"
        >
          {/* Header */}
          <DialogHeader className="shrink-0 flex flex-row items-center justify-between border-b border-[#262626] px-6 py-4">
            <div>
              <DialogTitle className="font-display text-lg font-semibold tracking-wide text-[#f4f1ec]">
                {isEditing ? `${t.characters.edit}: ${character.name}` : t.characters.new}
              </DialogTitle>
              <p className="mt-0.5 text-xs text-[#8e8e8e]">
                {t.characters.subtitle}
              </p>
            </div>
            <button
              data-testid="character-modal-close"
              onClick={handleRequestClose}
              className="rounded-lg p-1.5 text-[#888] transition-colors hover:bg-[#222] hover:text-[#f4f1ec]"
              aria-label={t.common.close}
            >
              <X size={18} />
            </button>
          </DialogHeader>

          {/* Body en dos columnas desplazable */}
          <div className="flex-1 overflow-y-auto p-6 grid grid-cols-1 gap-6 md:grid-cols-[320px_1fr]">
            {/* Columna Izquierda: Vistas y Recursos Visuales */}
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <span className="v-eyebrow text-xs text-[#a3a3a3]">
                  {t.characters.views} ({form.views.length})
                </span>
                {masterView && (
                  <span className="flex items-center gap-1 rounded bg-[#f5b544]/15 px-2 py-0.5 text-[10px] font-medium text-[#f5b544]">
                    <Star size={11} fill="currentColor" />
                    {t.characters.masterBadge}
                  </span>
                )}
              </div>

              {/* Vista previa de la Imagen Principal */}
              <div className="group relative overflow-hidden rounded-xl border border-[#303030] bg-[#1a1a1a]">
                {masterView?.assetId && assetById[masterView.assetId] ? (
                  <div className="relative aspect-[3/4] w-full">
                    <MediaFrame
                      asset={assetById[masterView.assetId]}
                      fit="contain"
                      className="h-full w-full bg-[#121212]"
                      showBadge={false}
                    />
                    <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between rounded-lg bg-black/60 px-2 py-1 text-[11px] backdrop-blur-md">
                      <span className="font-medium text-[#f4f1ec]">
                        {t.characters.viewTypes?.[masterView.viewType] || masterView.viewType}
                      </span>
                      <span className="text-[10px] text-[#f5b544] font-mono">Principal</span>
                    </div>
                  </div>
                ) : (
                  <div
                    data-testid="character-modal-no-master"
                    className="flex aspect-[3/4] w-full flex-col items-center justify-center gap-2 p-4 text-center text-[#666]"
                  >
                    <User size={42} className="opacity-40" />
                    <span className="text-xs font-medium text-[#888]">
                      {t.characters.noMaster}
                    </span>
                    <span className="text-[11px] text-[#666]">
                      Añade vistas para definir la apariencia de referencia.
                    </span>
                  </div>
                )}
              </div>

              {/* Acción explícita para usar como portada del proyecto */}
              {masterView?.assetId && (
                <div className="flex flex-col gap-1">
                  {targetProject?.coverAssetId === masterView.assetId ? (
                    <div
                      data-testid="character-cover-active-indicator"
                      className="flex items-center justify-center gap-1.5 rounded-lg border border-[#f5b544]/30 bg-[#f5b544]/10 py-1.5 px-2 text-[11px] font-medium text-[#f5b544]"
                    >
                      <Check size={13} />
                      <span>
                        {typeof t.characters.currentProjectCoverNamed === "function"
                          ? t.characters.currentProjectCoverNamed(targetProjectName)
                          : `Portada actual de ${targetProjectName}`}
                      </span>
                    </div>
                  ) : (
                    <button
                      type="button"
                      data-testid="character-modal-use-cover-btn"
                      disabled={settingCover}
                      onClick={() => handleSetProjectCover(masterView.assetId)}
                      className="v-btn-ghost v-btn-xs flex w-full items-center justify-center gap-1.5 border-[#303030] text-xs hover:border-[#f5b544] hover:text-[#f5b544]"
                    >
                      <Layers size={13} />
                      <span>
                        {typeof t.characters.useAsProjectCoverNamed === "function"
                          ? t.characters.useAsProjectCoverNamed(targetProjectName)
                          : `Usar como portada de: ${targetProjectName}`}
                      </span>
                    </button>
                  )}
                </div>
              )}

              {/* Tira / Cuadrícula de vistas adicionales */}
              {form.views.length > 0 && (
                <div className="flex flex-col gap-2">
                  <div className="grid grid-cols-3 gap-2">
                    {form.views.map((v) => {
                      const asset = assetById[v.assetId];
                      const isMaster = v.isMaster;
                      return (
                        <div
                          key={v.id}
                          data-testid={`character-view-item-${v.id}`}
                          className={`group relative flex flex-col overflow-hidden rounded-lg border bg-[#181818] transition-all ${
                            isMaster
                              ? "border-[#f5b544] shadow-[0_0_12px_rgba(245,181,68,0.15)]"
                              : "border-[#2a2a2a] hover:border-[#444]"
                          }`}
                        >
                          <div className="relative aspect-square w-full bg-[#111]">
                            <MediaFrame asset={asset} fit="contain" className="h-full w-full bg-[#121212]" showBadge={false} />
                            {/* Botón marcar como principal */}
                            <button
                              type="button"
                              data-testid={`character-view-set-master-${v.id}`}
                              onClick={() => handleSetMaster(v.id)}
                              title={isMaster ? t.characters.masterBadge : t.characters.setAsMaster}
                              className={`absolute left-1 top-1 rounded p-1 backdrop-blur-md transition-colors ${
                                isMaster
                                  ? "bg-[#f5b544] text-[#1a1206]"
                                  : "bg-black/60 text-white/70 opacity-0 group-hover:opacity-100 hover:text-white"
                              }`}
                            >
                              <Star size={12} fill={isMaster ? "currentColor" : "none"} />
                            </button>
                            {/* Botón usar como portada */}
                            <button
                              type="button"
                              data-testid={`character-view-set-cover-${v.id}`}
                              disabled={settingCover}
                              onClick={() => handleSetProjectCover(v.assetId)}
                              title={
                                targetProject?.coverAssetId === v.assetId
                                  ? (typeof t.characters.currentProjectCoverNamed === "function" ? t.characters.currentProjectCoverNamed(targetProjectName) : "Portada actual")
                                  : (typeof t.characters.useAsProjectCoverNamed === "function" ? t.characters.useAsProjectCoverNamed(targetProjectName) : `Usar como portada de ${targetProjectName}`)
                              }
                              className={`absolute left-7 top-1 rounded p-1 backdrop-blur-md transition-colors ${
                                targetProject?.coverAssetId === v.assetId
                                  ? "bg-[#f5b544] text-[#1a1206]"
                                  : "bg-black/60 text-white/70 opacity-0 group-hover:opacity-100 hover:text-white"
                              }`}
                            >
                              <Layers size={12} />
                            </button>
                            {/* Botón quitar vista */}
                            <button
                              type="button"
                              data-testid={`character-view-remove-${v.id}`}
                              onClick={() => handleRemoveView(v.id)}
                              title={t.characters.removeView}
                              className="absolute right-1 top-1 rounded bg-black/60 p-1 text-white/70 opacity-0 backdrop-blur-md transition-colors group-hover:opacity-100 hover:bg-rose-950/80 hover:text-rose-300"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                          {/* Selector de tipo de vista */}
                          <div className="p-1">
                            <select
                              data-testid={`character-view-type-select-${v.id}`}
                              value={v.viewType}
                              onChange={(e) => handleUpdateViewType(v.id, e.target.value)}
                              className="w-full rounded bg-[#202020] px-1 py-0.5 text-[10px] text-[#ccc] border border-[#333] focus:border-[#f5b544] focus:outline-none"
                            >
                              {CHARACTER_VIEW_TYPES.map((vt) => (
                                <option key={vt.id} value={vt.id}>
                                  {t.characters.viewTypes?.[vt.id] || vt.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Botones de acción para añadir imágenes */}
              <div className="flex flex-col gap-2 pt-1">
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="character-modal-pick-library"
                    onClick={() => setPickAssetOpen(true)}
                    className="v-btn-ghost v-btn-sm flex items-center justify-center gap-1.5 border-[#303030] text-xs hover:border-[#f5b544] hover:text-[#f5b544]"
                  >
                    <ImagePlus size={14} />
                    {t.characters.pickFromLibrary}
                  </button>
                  <button
                    type="button"
                    data-testid="character-modal-upload-btn"
                    disabled={uploading}
                    onClick={() => fileInputRef.current?.click()}
                    className="v-btn-ghost v-btn-sm flex items-center justify-center gap-1.5 border-[#303030] text-xs hover:border-[#f5b544] hover:text-[#f5b544]"
                  >
                    <Upload size={14} className={uploading ? "animate-bounce" : ""} />
                    {uploading ? t.characters.uploading : t.characters.uploadImage}
                  </button>
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  data-testid="character-file-input"
                />
                {uploadError && (
                  <p className="text-[11px] text-rose-400">{uploadError}</p>
                )}

                <div className="rounded-lg border border-[#262626] bg-[#181818] p-2.5 text-[11px] text-[#8e8e8e]">
                  <div className="flex items-start gap-1.5">
                    <Info size={13} className="mt-0.5 shrink-0 text-[#f5b544]" />
                    <span>{t.characters.referenceNote}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Columna Derecha: Identidad, Rasgos Físicos, Vestuario y Ámbito */}
            <div className="flex flex-col gap-4">
              {/* Categoría / Kind */}
              <div>
                <label className="v-eyebrow mb-1.5 block text-xs text-[#a3a3a3]">
                  Categoría de recurso
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {CHARACTER_KINDS.map((k) => (
                    <button
                      key={k}
                      type="button"
                      data-testid={`character-kind-${k}`}
                      onClick={() => setForm((prev) => ({ ...prev, kind: k }))}
                      className={`v-btn-ghost v-btn-sm text-xs transition-colors ${
                        form.kind === k
                          ? "border-[#f5b544] bg-[#f5b544]/10 text-[#f5b544]"
                          : "text-[#888] hover:text-[#ccc]"
                      }`}
                    >
                      {t.characters.kinds[k]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Asignación de Proyecto (ámbito) */}
              {state.projects.length > 0 && (
                <div>
                  <label className="v-eyebrow mb-1.5 flex items-center gap-1.5 text-xs text-[#a3a3a3]">
                    <FolderKanban size={13} />
                    {t.characters.projectScope}
                  </label>
                  <select
                    data-testid="character-modal-project-select"
                    value={form.projectId || ""}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        projectId: e.target.value || null,
                        scope: e.target.value ? "project" : "pending_review",
                      }))
                    }
                    className="v-input w-full bg-[#181818] text-xs"
                  >
                    {state.projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name || "Proyecto sin título"} ({p.id})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Nombre y @handle */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="v-eyebrow mb-1.5 block text-xs text-[#a3a3a3]">
                    {t.common.name} <span className="text-[#f5b544]">*</span>
                  </label>
                  <input
                    data-testid="character-modal-name"
                    className="v-input w-full"
                    placeholder="Ej. Detective Sarah Chen"
                    value={form.name}
                    onChange={handleNameChange}
                    autoFocus
                  />
                </div>
                <div>
                  <label className="v-eyebrow mb-1.5 block text-xs text-[#a3a3a3]">
                    {t.common.handle} <span className="text-[#f5b544]">*</span>
                  </label>
                  <div className="flex items-center gap-1">
                    <span className="flex h-10 w-8 items-center justify-center rounded-lg border border-[#303030] bg-[#1a1a1a] font-mono text-xs text-[#f5b544]">
                      @
                    </span>
                    <input
                      data-testid="character-modal-handle"
                      className="v-input w-full font-mono text-sm text-[#f5b544]"
                      placeholder="sarah_chen"
                      value={form.handle}
                      onChange={handleHandleChange}
                    />
                  </div>
                  {!collisionResult.valid ? (
                    <p data-testid="character-handle-collision-error" className="mt-1 flex items-center gap-1 text-[11px] text-rose-400">
                      <AlertTriangle size={12} />
                      {collisionResult.reason}
                    </p>
                  ) : (
                    <p className="mt-1 text-[11px] text-[#777]">
                      {t.characters.handleHint}
                    </p>
                  )}
                </div>
              </div>

              {/* Autocompletar identidad y vestuario desde imagen */}
              <div className="flex flex-col gap-2 rounded-lg border border-[#2a2a2a] bg-[#171717] p-3">
                <div className="flex items-center justify-between">
                  <span className="v-eyebrow text-xs text-[#a3a3a3] flex items-center gap-1.5">
                    <Sparkles size={13} className="text-[#f5b544]" />
                    {t.characters.analyzeSectionTitle || "Autocompletar ficha desde imagen"}
                  </span>
                  {activeVisionModel && (
                    <span className="text-[10px] text-[#f5b544] font-mono">
                      {typeof t.characters.modelCostEstimate === "function"
                        ? t.characters.modelCostEstimate(activeVisionModel.model_label || activeVisionModel.model_id, activeVisionModel.cost_estimate_usd || "0.002")
                        : `Modelo: ${activeVisionModel.model_label || activeVisionModel.model_id}`}
                    </span>
                  )}
                </div>

                {/* Si no hay modelo de visión conectado */}
                {!activeVisionModel && !window.__VEYRA_ENABLE_MOCK_VISION__ ? (
                  <div data-testid="character-no-vision-notice" className="rounded border border-[#262626] bg-[#121212] p-2.5 text-[11px] text-[#8e8e8e]">
                    <div className="flex items-start gap-1.5">
                      <Info size={13} className="mt-0.5 shrink-0 text-[#888]" />
                      <span>{t.characters.noVisionModelNotice}</span>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    data-testid="character-modal-analyze-image"
                    disabled={analyzingImage || !masterView?.assetId}
                    onClick={handleAnalyzeImage}
                    className="v-btn-ghost v-btn-sm w-full border-[#333] hover:border-[#f5b544] text-[#f5b544] flex items-center justify-center gap-1.5 text-xs"
                  >
                    <Sparkles size={13} className={analyzingImage ? "animate-spin" : ""} />
                    {analyzingImage ? (t.characters.analyzingImage || "Analizando...") : (t.characters.analyzeImageBtn || "Analizar imagen y completar ficha")}
                  </button>
                )}

                {/* Tarjeta de propuesta de análisis si existe */}
                {analysisProposal && (
                  <div data-testid="character-analysis-proposal" className="mt-1 flex flex-col gap-2.5 rounded-lg border border-[#f5b544]/40 bg-[#1f1a10] p-3 text-xs">
                    <div className="flex items-start justify-between">
                      <div>
                        <span className="font-semibold text-xs text-[#f5b544] flex items-center gap-1">
                          <Sparkles size={12} />
                          {t.characters.proposalTitle}
                        </span>
                        <p className="text-[10px] text-[#bbb] mt-0.5">
                          {t.characters.proposalSubtitle}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setAnalysisProposal(null)}
                        className="text-[#888] hover:text-[#eee] p-1"
                        title={t.characters.discardProposal}
                      >
                        <X size={13} />
                      </button>
                    </div>

                    {/* Bloque Identidad */}
                    <div className="rounded border border-[#3a2e16] bg-[#14120a] p-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] font-medium text-[#f4f1ec]">
                          {t.characters.identitySection}
                        </span>
                        <button
                          type="button"
                          data-testid="character-apply-identity-btn"
                          onClick={handleApplyIdentity}
                          className="v-btn-ghost v-btn-xs text-[10px] text-[#f5b544] border-[#55421c] hover:bg-[#f5b544]/15"
                        >
                          {t.characters.applyToIdentity}
                        </button>
                      </div>
                      <p className="text-[11px] text-[#ccc] leading-relaxed italic">
                        "{analysisProposal.identity_traits}"
                      </p>
                    </div>

                    {/* Bloque Vestuario */}
                    <div className="rounded border border-[#3a2e16] bg-[#14120a] p-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] font-medium text-[#f4f1ec]">
                          {t.characters.outfitSection}
                        </span>
                        <button
                          type="button"
                          data-testid="character-apply-outfit-btn"
                          onClick={handleApplyOutfit}
                          className="v-btn-ghost v-btn-xs text-[10px] text-[#f5b544] border-[#55421c] hover:bg-[#f5b544]/15"
                        >
                          {t.characters.applyToOutfit}
                        </button>
                      </div>
                      <p className="text-[11px] text-[#ccc] leading-relaxed italic">
                        "{analysisProposal.outfit}"
                      </p>
                    </div>

                    <p className="text-[10px] text-[#8e8e8e]">
                      {t.characters.visibleTraitsNotice}
                    </p>
                  </div>
                )}
              </div>

              {/* Identidad y Rasgos Físicos */}
              <div>
                <label className="v-eyebrow mb-1.5 block text-xs text-[#a3a3a3]">
                  {t.characters.identitySection}
                </label>
                <textarea
                  data-testid="character-modal-description"
                  className="v-textarea w-full text-xs"
                  rows={3}
                  placeholder={t.characters.identityPlaceholder}
                  value={form.description}
                  onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                />
              </div>

              {/* Vestuario Base Separado */}
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="v-eyebrow text-xs text-[#a3a3a3]">
                    {t.characters.outfitSection}
                  </label>
                </div>
                <textarea
                  data-testid="character-modal-outfit"
                  className="v-textarea w-full text-xs"
                  rows={2}
                  placeholder={t.characters.outfitPlaceholder}
                  value={form.outfit}
                  onChange={(e) => setForm((prev) => ({ ...prev, outfit: e.target.value }))}
                />
                <p className="mt-1 text-[11px] text-[#777]">
                  {t.characters.outfitHint}
                </p>
              </div>

              {/* Aviso de menciones @ */}
              <div className="mt-auto rounded-lg border border-[#222] bg-[#161616] p-2.5 text-[11px] text-[#888]">
                <span className="font-mono text-[#f5b544]">@{form.handle || "personaje"}</span> · {t.characters.mentionNote}
              </div>
            </div>
          </div>

          {/* Footer de Acciones siempre visible */}
          <div className="shrink-0 flex items-center justify-end gap-3 border-t border-[#262626] bg-[#101010] px-6 py-3.5">
            <button
              type="button"
              data-testid="character-modal-cancel"
              onClick={handleRequestClose}
              className="v-btn-ghost v-btn-sm text-xs"
            >
              {t.common.cancel}
            </button>
            <button
              type="button"
              data-testid="character-modal-save"
              disabled={!form.name.trim() || !form.handle.trim() || !collisionResult.valid}
              onClick={handleSave}
              className="v-btn-primary v-btn-sm text-xs"
            >
              <Check size={14} />
              {t.common.save}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Asset Picker para elegir vistas existentes de la biblioteca */}
      <AssetPicker
        open={pickAssetOpen}
        onOpenChange={setPickAssetOpen}
        onPick={handleAddPickedAsset}
      />

      {/* Confirmación de descarte de cambios */}
      <ConfirmDialog
        open={discardConfirmOpen}
        onOpenChange={setDiscardConfirmOpen}
        title={t.characters.discardConfirmTitle}
        body={t.characters.discardConfirmBody}
        confirmLabel={t.characters.discardChanges}
        cancelLabel={t.characters.continueEditing}
        onConfirm={() => {
          setDiscardConfirmOpen(false);
          onOpenChange(false);
        }}
      />
    </>
  );
};

export default CharacterModal;
